
import React, { useState, useRef, useCallback } from 'react';
import './PostCard.css';
import { Post, useApp } from '../../context/AppContext';
import UnlockModal from '../UnlockModal/UnlockModal';
import ImageModal from '../ImageModal/ImageModal';
import CommentsSheet from '../CommentsSheet/CommentsSheet';
import { useVideoThumbnail } from '../../hooks/useVideoThumbnail';
import VideoPreview from '../VideoPreview/VideoPreview';
import { toast } from '../Toast/Toast';
import { haptic } from '../../utils/haptic';

interface PostCardProps {
  post: Post;
}

const AVATAR_FALLBACK = 'https://onlyjalen-api.jalen1wa.workers.dev/api/media/profile/jalen-avatar.jpg';
// data: URLs with raw '#' break parsers (the '#' starts a fragment), so we
// hand encodeURIComponent unescaped chars and let it encode '#' once. The
// previous version pre-encoded '#' as '%23' inside the template, which
// encodeURIComponent then turned into '%2523' — yielding a malformed SVG
// that the browser swapped for its native broken-image X icon.
const MEDIA_FALLBACK_DATA = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 800'>
    <defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='#1a0a2e'/><stop offset='100%' stop-color='#14082a'/></linearGradient></defs>
    <rect width='800' height='800' fill='url(#g)'/>
    <g fill='#8a7fa5' font-family='-apple-system,Segoe UI,sans-serif' text-anchor='middle'>
      <text x='400' y='400' font-size='42' font-weight='700'>Media unavailable</text>
      <text x='400' y='450' font-size='22'>Try refreshing the feed</text>
    </g>
  </svg>`
);

const PostCard: React.FC<PostCardProps> = ({ post }) => {
  const { adminUser, currentUser, isAdminView, likePost, savePost } = useApp();
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showCommentsSheet, setShowCommentsSheet] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Double-tap-to-like state: fires a heart-burst overlay on the media.
  // The burst auto-clears after the animation duration matches the CSS.
  const [heartBurst, setHeartBurst] = useState(false);
  // Like-button pulse: short scale-pop on every like toggle, used as
  // micro-feedback even when the user clicks the heart button directly.
  const [likePulse, setLikePulse] = useState(false);
  const lastTapRef = useRef<number>(0);
  const burstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Per-image load failure flags. When an image errors out we collapse the
  // media tile to a thin inline notice instead of leaving a giant blank
  // square — broken images were stacking ~600px voids down the feed.
  // Errors keyed by URL (not index) so a parent re-fetch that reorders
  // images doesn't mis-mark a working image as broken.
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<string, boolean>>({});
  // Tracks which URLs have successfully loaded so we can switch off the
  // skeleton-shimmer background once the real image paints. CSS-only
  // approaches (e.g. :has(img[src])) match from initial render because the
  // src attribute is set immediately — they never actually fire.
  const [imageLoaded, setImageLoaded] = useState<Record<string, boolean>>({});
  // useVideoThumbnail returns { thumbnail, status } now — we destructure
  // for the still-thumbnail render below. VideoPreview handles its own
  // extraction when it's used in card contexts.
  const { thumbnail: videoThumbnail } = useVideoThumbnail(
    post.mediaType === 'video' && post.mediaUrl ? post.mediaUrl : undefined
  );

  const canView = !post.isLocked || currentUser.isSubscribed;
  const images = post.mediaUrls || (post.mediaUrl ? [post.mediaUrl] : []);
  const isLiked = post.likedBy.includes(currentUser.id);
  const isSaved = post.savedBy.includes(currentUser.id);
  const canDownload = canView && currentUser.isSubscribed && (images.length > 0 || (post.mediaType === 'video' && post.mediaUrl));

  // Trigger heart-pulse animation on the like button. We always run it
  // (independent of liked state) so a double-tap that goes from
  // unliked→liked AND a direct button tap both feel responsive.
  const triggerLikePulse = useCallback(() => {
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    setLikePulse(true);
    pulseTimerRef.current = setTimeout(() => setLikePulse(false), 350);
  }, []);

  // Direct like — used by the action-bar button. Adds haptic + pulse.
  const handleLikeClick = useCallback(() => {
    haptic('tap');
    triggerLikePulse();
    likePost(post.id);
  }, [post.id, likePost, triggerLikePulse]);

  // Insta-style double-tap on the media tile.
  //
  // Tap interaction model:
  //   1st tap   → schedule "open image modal" 220ms in the future
  //   2nd tap within 300ms → cancel the scheduled open, burst+like
  //
  // The 220ms delay is barely perceptible on a real single-tap but
  // gives the second tap of a double-tap enough room to suppress the
  // modal. If user already liked the post, the burst re-fires without
  // toggling off (treat as "applause").
  const singleTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMediaTap = useCallback(() => {
    if (!canView) return;
    const now = Date.now();
    const isDoubleTap = now - lastTapRef.current < 300;
    lastTapRef.current = now;
    if (isDoubleTap) {
      if (singleTapTimerRef.current) {
        clearTimeout(singleTapTimerRef.current);
        singleTapTimerRef.current = null;
      }
      haptic('tap');
      if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
      setHeartBurst(true);
      burstTimerRef.current = setTimeout(() => setHeartBurst(false), 750);
      if (!isLiked) {
        triggerLikePulse();
        likePost(post.id);
      }
      return;
    }
    if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
    singleTapTimerRef.current = setTimeout(() => {
      setShowImageModal(true);
      singleTapTimerRef.current = null;
    }, 220);
  }, [canView, isLiked, post.id, likePost, triggerLikePulse]);

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const formatCount = (n: number) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n > 0 ? String(n) : '';
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Post by ${adminUser.displayName}`,
          text: post.content || 'Check out this post!',
          url: window.location.href,
        });
      } catch {
        setShowShareMenu(true);
      }
    } else {
      setShowShareMenu(true);
    }
  };

  const handleDownload = async () => {
    if (downloading) return;
    const url = post.mediaType === 'video' && post.mediaUrl ? post.mediaUrl : images[imageIndex];
    if (!url) return;
    setDownloading(true);
    try {
      const res = await fetch(url, { credentials: 'omit' });
      const blob = await res.blob();
      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `onlyjalen-${post.id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.warn('Download failed, opening in new tab as fallback', err);
      window.open(url, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <article className="post-card animate-fade-in">
      <div className="post-header">
        <div className="post-avatar-container">
          <img
            src={adminUser.avatar}
            alt={adminUser.displayName}
            className="post-avatar"
            onError={e => { (e.target as HTMLImageElement).src = AVATAR_FALLBACK; }}
          />
          {adminUser.online && <div className="avatar-online-dot" />}
        </div>
        <div className="post-user-info">
          <div className="post-user-name">
            {adminUser.displayName}
            <svg className="verified-badge" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <div className="post-meta">
            <span className="post-time">{formatTime(post.timestamp)}</span>
            {post.isLocked && !canView && <span className="locked-badge">Premium</span>}
          </div>
        </div>
        <button
          className="post-more-btn"
          aria-label="More options"
          aria-expanded={showMoreMenu}
          onClick={() => setShowMoreMenu(v => !v)}
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="6" r="2"/>
            <circle cx="12" cy="12" r="2"/>
            <circle cx="12" cy="18" r="2"/>
          </svg>
        </button>
        {showMoreMenu && (
          <>
            <div className="post-more-backdrop" onClick={() => setShowMoreMenu(false)} />
            <div className="post-more-menu" role="menu">
              <button
                className="post-more-item"
                role="menuitem"
                onClick={async () => {
                  setShowMoreMenu(false);
                  try {
                    await navigator.clipboard?.writeText(window.location.href);
                    toast.success('Link copied');
                  } catch {
                    toast.error('Could not copy link');
                  }
                }}
              >
                Copy link
              </button>
              <button
                className="post-more-item"
                role="menuitem"
                onClick={() => { setShowMoreMenu(false); handleShare(); }}
              >
                Share
              </button>
              {!isAdminView && (
                <button
                  className="post-more-item post-more-item--danger"
                  role="menuitem"
                  onClick={() => {
                    setShowMoreMenu(false);
                    toast.success('Report submitted — thanks');
                  }}
                >
                  Report
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {post.content && <p className="post-content">{post.content}</p>}

      {post.mediaType === 'video' && post.mediaUrl && canView ? (
        <div className="post-media post-media--video" onClick={e => e.stopPropagation()}>
          {videoPlaying ? (
            <video
              src={post.mediaUrl}
              controls
              autoPlay
              preload="auto"
              playsInline
              className="post-video"
              style={{ width: '100%', maxHeight: '480px', objectFit: 'contain', background: '#000', display: 'block' }}
            />
          ) : (
            /* Thumbnail tile — cover is correct here. Hover-preview gives the
               GIF-like animated peek; tap fully plays inline. */
            <div className="post-video-tile" onClick={() => setVideoPlaying(true)}>
              <VideoPreview
                src={post.mediaUrl}
                posterUrl={post.posterUrl}
                hoverPreview
                alt=""
              />
            </div>
          )}
        </div>
      ) : images.length > 0 ? (
        imageLoadErrors[images[imageIndex]] ? (
          /* Collapsed broken-media placeholder. Keeps the card compact
             instead of reserving a giant aspect-ratio:1 square that just
             shows a dark fallback SVG and turns the feed into a void column. */
          <div className="post-media-broken">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
            <span>Media unavailable</span>
          </div>
        ) : (
        <div
          className={`post-media ${!canView ? 'locked' : ''} ${imageLoaded[images[imageIndex]] ? 'loaded' : ''}`}
          onClick={handleMediaTap}
        >
          <img
            src={images[imageIndex]}
            alt=""
            loading="lazy"
            onLoad={() => setImageLoaded(prev => ({ ...prev, [images[imageIndex]]: true }))}
            onError={() => setImageLoadErrors(prev => ({ ...prev, [images[imageIndex]]: true }))}
          />
          {heartBurst && (
            <div className="heart-burst" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
              </svg>
            </div>
          )}
          {images.length > 1 && canView && (
            <>
              <div className="media-indicators">
                {images.map((_, i) => (
                  <span key={i} className={`indicator ${i === imageIndex ? 'active' : ''}`} />
                ))}
              </div>
              <button
                className="media-nav prev"
                aria-label="Previous image"
                onClick={e => { e.stopPropagation(); setImageIndex(i => Math.max(0, i - 1)); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              </button>
              <button
                className="media-nav next"
                aria-label="Next image"
                onClick={e => { e.stopPropagation(); setImageIndex(i => Math.min(images.length - 1, i + 1)); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            </>
          )}
          {!canView && (
            <div className="locked-overlay" onClick={() => setShowUnlockModal(true)}>
              <div className="locked-content">
                <div className="lock-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                </div>
                <span className="locked-text">Unlock for ${post.price}</span>
                <button className="unlock-btn">Unlock Now</button>
              </div>
            </div>
          )}
        </div>
        )
      ) : null}

      {!isAdminView && (
        <div className="post-actions">
          <button
            className={`action-btn like-btn ${isLiked ? 'liked' : ''} ${likePulse ? 'pulsing' : ''}`}
            onClick={handleLikeClick}
            aria-label={isLiked ? 'Unlike' : 'Like'}
          >
            <svg viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
            </svg>
            {formatCount(post.likes) && <span>{formatCount(post.likes)}</span>}
          </button>

          <button
            className="action-btn comment-btn"
            onClick={() => setShowCommentsSheet(true)}
            aria-label="Comments"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
            {formatCount(post.comments) && <span>{formatCount(post.comments)}</span>}
          </button>

          <button
            className={`action-btn save-btn ${isSaved ? 'saved' : ''}`}
            onClick={() => savePost(post.id)}
            aria-label={isSaved ? 'Unsave' : 'Save'}
            title={isSaved ? 'Saved to your collection' : 'Save to your collection'}
          >
            <svg viewBox="0 0 24 24" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
            </svg>
          </button>

          {canDownload && (
            <button
              className={`action-btn download-btn ${downloading ? 'downloading' : ''}`}
              onClick={handleDownload}
              disabled={downloading}
              aria-label="Download"
              title="Download to your device"
            >
              {downloading ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
                  <path d="M21 12a9 9 0 11-6.219-8.56"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              )}
            </button>
          )}

          <button className="action-btn share-btn" onClick={handleShare} aria-label="Share">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/>
              <polyline points="16,6 12,2 8,6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
          </button>
        </div>
      )}

      {showShareMenu && (
        <div className="share-menu-backdrop" onClick={() => setShowShareMenu(false)}>
          <div className="share-menu" onClick={e => e.stopPropagation()}>
            <button className="share-option" onClick={async () => {
              setShowShareMenu(false);
              try {
                await navigator.clipboard?.writeText(window.location.href);
                toast.success('Link copied');
              } catch {
                toast.error('Could not copy link');
              }
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
              </svg>
              Copy Link
            </button>
            <button className="share-option" onClick={() => { window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.content || '')}`, '_blank', 'noopener,noreferrer'); setShowShareMenu(false); }}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Share to X
            </button>
          </div>
        </div>
      )}

      {showUnlockModal && <UnlockModal post={post} onClose={() => setShowUnlockModal(false)} />}
      {showImageModal && images.length > 0 && (
        <ImageModal
          images={images}
          initialIndex={imageIndex}
          onClose={() => setShowImageModal(false)}
        />
      )}
      {showCommentsSheet && (
        <CommentsSheet post={post} onClose={() => setShowCommentsSheet(false)} />
      )}
    </article>
  );
};

export default PostCard;
